/**
 * Type definitions for JSON-driven form rendering
 */
/**
 * Gets the default value for a field based on its type
 */
const getDefaultValueForType = (type) => {
    switch (type) {
        case 'checkbox':
        case 'dropdown-multi':
            return [];
        case 'termsCheckbox':
            return false;
        default:
            return '';
    }
};
/**
 * Helper to initialize field values recursively (including children)
 * @param fields - Array of fields to initialize
 * @param values - Object to populate with initialized values
 * @param initialData - Optional initial data to override defaults
 */
const initializeFieldsRecursive = (fields, values, initialData) => {
    for (const field of fields) {
        // Skip divider fields - they don't have values
        if (field.type === 'divider')
            continue;
        // Hidden fields with a `value` property always use that fixed value
        if (field.type === 'hidden' && field.value !== undefined) {
            values[field.fieldName] = field.value;
        }
        // Priority: initialData > defaultValue > type default
        else if (initialData && field.fieldName in initialData) {
            values[field.fieldName] = initialData[field.fieldName];
        }
        else if (field.defaultValue !== undefined) {
            values[field.fieldName] = field.defaultValue;
        }
        else {
            values[field.fieldName] = getDefaultValueForType(field.type);
        }
        // Initialize child fields if this is a dropdown with options that have children
        if (field.type === 'dropdown' && field.options) {
            for (const option of field.options) {
                if (option.children) {
                    initializeFieldsRecursive(option.children, values, initialData);
                }
            }
        }
    }
};
/**
 * Initializes form values from a schema definition
 * Uses defaultValue from schema if provided, otherwise uses type-appropriate defaults
 * Also initializes conditional child fields
 * @param schema - Form schema definition
 * @param initialData - Optional initial data to override defaults
 * @returns Initialized form values
 */
export const initializeFormValues = (schema, initialData) => {
    const values = {};
    initializeFieldsRecursive(schema.fields, values, initialData);
    return values;
};
/**
 * Helper to validate a single field
 * @param field - Field definition to validate
 * @param value - Current value of the field
 * @returns true if the field is valid
 */
const validateField = (field, value) => {
    if (!field.validation?.required)
        return true;
    if (field.type === 'checkbox' || field.type === 'dropdown-multi') {
        return Array.isArray(value) && value.length > 0;
    }
    else if (field.type === 'termsCheckbox') {
        return value === true;
    }
    else if (typeof value === 'string') {
        return value.trim() !== '';
    }
    else {
        return value !== undefined && value !== null;
    }
};
/**
 * Helper to validate conditional child fields
 * @param fields - Array of fields to validate (including conditional children)
 * @param values - Form values
 * @returns true if all visible required fields are valid
 */
const validateFieldsRecursive = (fields, values) => {
    for (const field of fields) {
        // Validate the field itself
        if (!validateField(field, values[field.fieldName])) {
            return false;
        }
        // If this is a dropdown with children, validate visible children
        if (field.type === 'dropdown' && field.options) {
            const selectedValue = values[field.fieldName];
            if (selectedValue) {
                const selectedOption = field.options.find(opt => opt.value === selectedValue);
                if (selectedOption?.children) {
                    // Recursively validate child fields
                    if (!validateFieldsRecursive(selectedOption.children, values)) {
                        return false;
                    }
                }
            }
        }
    }
    return true;
};
/**
 * Validates form values against a schema
 * Handles conditional fields (dropdown children) - only validates visible fields
 * @param values - Form values to validate
 * @param schema - Form schema with validation rules
 * @returns true if all required visible fields are filled
 */
export const validateFormValues = (values, schema) => {
    return validateFieldsRecursive(schema.fields, values);
};
